import React, { useEffect, useState } from 'react';

import { ReactComponent as Dot } from '/src/images/icons/dot.svg';
import { ReactComponent as IconPlaceholder } from '/src/images/icons/icon-placeholder.svg';

import fetchIkeaProductFeed from '../../../helpers/ikea-product-feed/ikea-product-feed-helpers';
import { IkeaProductCard } from '../../../helpers/ikea-product-feed/interfaces';
import { CardProduct } from '../../cards/card-product';
import { TenantIkeaProductCard } from '../../cards/tenant-ikea-product-card';

export interface IkeaProductCardsListInHomePageProps {
  businessUnitCode?: string;
  primaryLanguage?: string;
  node_locale: string;
  buyNowText: string;
  variant: string;
  cardHeader?: string;
  pageName: string;
}

const IkeaProductCardInHomePage = ({
  businessUnitCode,
  primaryLanguage,
  node_locale,
  buyNowText,
  variant,
  cardHeader,
  pageName,
}: IkeaProductCardsListInHomePageProps) => {
  const [products, setProducts] = useState<IkeaProductCard[]>([]);
  const [currentProduct, setCurrentProduct] = useState<number>(0);
  const [loaderStatus, setLoaderStatus] = useState<boolean>(true);

  const numberOfProducts = 3;

  useEffect(() => {
    setLoaderStatus(true);
    const fetchProductsData = async () => {
      const fetchedProducts = await fetchIkeaProductFeed(
        node_locale,
        businessUnitCode,
        primaryLanguage || '',
        numberOfProducts,
      );
      setProducts(fetchedProducts);
      setLoaderStatus(false);
    };

    fetchProductsData();
  }, [node_locale, businessUnitCode, primaryLanguage]);

  useEffect(() => {
    if (!loaderStatus) {
      const timeout = setTimeout(() => {
        setCurrentProduct((prevIndex) => (prevIndex + 1) % products.length);
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [currentProduct, products]);

  const SliderNavigation = ({ styleClass }: { styleClass: string }) => {
    return (
      <ul
        className={`absolute ${styleClass} left-0 right-0 z-10 flex justify-center`}
      >
        {products.map((_, index) => (
          <li
            key={`slider-navigation-indicator-${index}`}
            className="mx-1.5 flex items-center"
            onClick={() => setCurrentProduct(index)}
          >
            {index === currentProduct ? (
              <IconPlaceholder className="brightness-1 invert" />
            ) : (
              <Dot className="brightness-50 invert" />
            )}
          </li>
        ))}
      </ul>
    );
  };

  const SkeletonLoader = () => (
    <div className="mb-5 aspect-0.75/1 h-auto w-full rounded-xl bg-gradient-to-r from-[rgba(234,227,218,0.2)] via-[rgba(234,227,218,0.5)] to-[rgba(234,227,218,0.2)] bg-[length:200%_100%] animate-wave"></div>
  );

  const TenantSkeletonLoader = () => (
    <div className="ikea w-full xl:min-h-[22rem] lg:min-h-[24rem] md:min-h-[22rem] min-h-[20rem]  flex cursor-pointer flex-col rounded-xl bg-background-primary-light p-5 animate-pulse"></div>
  );

  return (
    <>
      {variant == 'homepage' ? (
        <>
          {products.length == 0 && loaderStatus && <SkeletonLoader />}
          {products && products.length > 0 && (
            <div className="relative mb-5">
              <div className="transition-all delay-200 duration-500">
                {products?.map((product, index) => (
                  <div
                    key={product.id}
                    className={`${
                      index === currentProduct ? 'block' : 'hidden'
                    } animate-fadeIn`}
                  >
                    <CardProduct
                      card={product}
                      cardIndex={index}
                      buyNowText={buyNowText}
                      showCarousel={true}
                      pageName={pageName}
                    />
                  </div>
                ))}
              </div>
              <SliderNavigation styleClass={'bottom-4'} />
            </div>
          )}
        </>
      ) : (
        <>
          {products.length == 0 && loaderStatus && <TenantSkeletonLoader />}
          {products && products.length > 0 && (
            <div className="relative">
              <div className="transition-all delay-200 duration-500">
                {products?.map((product, index) => (
                  <div
                    key={product.id}
                    className={`${
                      index === currentProduct ? 'block' : 'hidden'
                    } animate-fadeIn`}
                  >
                    <TenantIkeaProductCard
                      card={product}
                      cardIndex={index}
                      buyNowText={buyNowText}
                      showCarousel={true}
                      cardHeader={cardHeader ?? ''}
                      pageName={pageName}
                    />
                  </div>
                ))}
              </div>
              <SliderNavigation styleClass={'bottom-5'} />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default IkeaProductCardInHomePage;
